import { useMemo } from 'react';
import { Box } from '@chakra-ui/react';

import { InboxLayout } from '@/components/inbox/InboxLayout';
import { TicketFilterSelect } from '@/components/inbox/TicketFilterSelect';
import { ThreadList } from '@/components/inbox/ThreadList';
import { ThreadView } from '@/components/inbox/ThreadView';
import { Composer } from '@/components/inbox/Composer';

import { useInboxStore } from '@/store/inbox';
import type { InboxFilterKey, InboxThread, InboxMessage } from '@/components/inbox/types';

const filterThread = (f: InboxFilterKey, t: InboxThread) => {
  const isTrashed = !!t.deletedAt;

  if (f === 'trash') return isTrashed;
  if (f === 'all') return !isTrashed;
  if (isTrashed) return false;

  if (f === 'new') return t.status === 'new';
  if (f === 'needs_reply') return t.status === 'needs_reply';
  if (f === 'draft') return !!t.draft?.body?.trim();
  if (f === 'closed') return t.status === 'closed';

  return true;
};

const includesSearch = (q: string, t: InboxThread) => {
  const s = q.trim().toLowerCase();
  if (!s) return true;

  return (
    (t.customerName || '').toLowerCase().includes(s) ||
    (t.customerEmail || '').toLowerCase().includes(s) ||
    (t.lastMessageSnippet || '').toLowerCase().includes(s)
  );
};

export default function InboxPage() {
  const threads = useInboxStore((s) => s.threads);
  const messagesMap = useInboxStore((s) => s.messages);

  const activeThreadId = useInboxStore((s) => s.activeThreadId);
  const setActiveThread = useInboxStore((s) => s.setActiveThread);
  const markThreadRead = useInboxStore((s) => s.markThreadRead);
  const addMessage = useInboxStore((s) => s.addMessage);

  const filter = useInboxStore((s) => s.filter);
  const setFilter = useInboxStore((s) => s.setFilter);
  const search = useInboxStore((s) => s.search);
  const setSearch = useInboxStore((s) => s.setSearch);

  // drafts (Option B)
  const drafts = useInboxStore((s) => s.drafts);
  const setDraft = useInboxStore((s) => s.setDraft);
  const clearDraft = useInboxStore((s) => s.clearDraft);

  const activeThread = useMemo(() => {
    return threads.find((t) => t.id === activeThreadId) || null;
  }, [threads, activeThreadId]);

  const activeMessages = useMemo((): InboxMessage[] => {
    if (!activeThreadId) return [];
    return messagesMap[activeThreadId] || [];
  }, [messagesMap, activeThreadId]);

  const visibleThreads = useMemo(() => {
    return threads
      .filter((t) => filterThread(filter, t))
      .filter((t) => includesSearch(search, t));
  }, [threads, filter, search]);

  const composerValue = useMemo(() => {
    if (!activeThreadId) return '';
    return drafts[activeThreadId]?.body ?? '';
  }, [drafts, activeThreadId]);

  return (
    <Box>
      <InboxLayout
        left={
          <>
            <TicketFilterSelect
              value={filter}
              onChange={(v) => setFilter(v)}
              search={search}
              onSearchChange={(v) => setSearch(v)}
            />

            <ThreadList
              threads={visibleThreads}
              activeThreadId={activeThreadId}
              onSelectThread={(id) => {
                setActiveThread(id);
                markThreadRead(id);
              }}
            />
          </>
        }
        middle={<ThreadView threadId={activeThreadId} messages={activeMessages} />}
        bottom={
          <Composer
            threadId={activeThreadId}
            toName={activeThread?.customerName ?? ''}
            toEmail={activeThread?.customerEmail ?? ''}
            value={composerValue}
            onChange={(v) => {
              if (!activeThreadId) return;
              setDraft(activeThreadId, v);
            }}
            onSend={(body) => {
              if (!activeThreadId) return;

              addMessage(activeThreadId, {
                id: `m-${Date.now()}`,
                threadId: activeThreadId,
                fromType: 'agent',
                fromName: 'Anna Smith',
                fromEmail: 'support@actionlabs.com',
                body,
                createdAt: new Date().toISOString(),
              });

              //clear draft for this thread after sending
              clearDraft(activeThreadId);
            }}
          />
        }
      />
    </Box>
  );
}
